import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import type { FolderTreeItem } from 'src/app/shared/services/folder.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { RvDraggableModule } from 'src/app/rv-draggable/rv-draggable.module';

interface FolderNode {
  name: string;
  path?: string;
  folderId?: string;
}

@Component({
    selector: 'folder-path',
    templateUrl: './folder-path.component.html',
    styleUrls: ['./folder-path.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatMenuModule, RvDraggableModule]
})
export class FolderPathComponent implements AfterViewInit, OnDestroy, OnChanges {

  @ViewChild('folderPath', { static: false }) folderPathElement!: ElementRef<HTMLElement>;

  private _originalTree: FolderNode[] = [];
  private _isInitialized = false;
  private _resizeTimeout;

  hiddenFolders: FolderNode[] = [];
  visibleFolders: FolderNode[] = [];
  showMenu = false;

  @Input() rootName: string = 'Storage';

  @Input() folderTree?: FolderTreeItem[];
  @Input() folderId?: string;

  @Input() set folderPath(path: string | undefined) {
    // Only process folderPath if we're not using the new folder tree approach
    if (this.folderTree && this.folderId) {
      return;
    }

    if (!path) {
      this._originalTree = [{
        path: '',
        name: this.rootName
      }];
      if (this._isInitialized) {
        setTimeout(() => this._checkForOverflow());
      }
      return;
    }

    this._originalTree = [{
      path: '',
      name: this.rootName
    }];
    const folders = path.split('/');
    let subPath = '';
    for (let folder of folders) {
      if (folder !== '') {
        subPath += folder + '/';
        let name = folder === StorageUtilsService.TRASH_PLACEHOLDER ? StorageUtilsService.TRASH_LABEL : folder;
        name = name.length > 20 ? name.substring(0, 20) + '...' : name;
        this._originalTree.push({
          path: subPath,
          name: name
        });
      }
    }
    if (this._isInitialized) {
      setTimeout(() => this._checkForOverflow());
    }
  }

  @Output() folderClicked: EventEmitter<string> = new EventEmitter<string>();

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
    }
    this._resizeTimeout = setTimeout(() => {
      this._checkForOverflow();
    }, 100);
  }

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngAfterViewInit() {
    this._isInitialized = true;
    setTimeout(() => this._checkForOverflow());
  }

  ngOnDestroy(): void {
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
    }
  }

  ngOnChanges() {
    if (this.folderTree) {
      this._buildTreeFromFolderStructure();
      if (this._isInitialized) {
        setTimeout(() => this._checkForOverflow());
      }
    }
  }

  private _buildTreeFromFolderStructure() {
    if (!this.folderTree) {
      return;
    }

    const pathToFolder = this._findPathToFolder(this.folderTree, this.folderId);

    this._originalTree = [{
      folderId: '',
      name: this.rootName
    }];

    pathToFolder.forEach((folder, index) => {
      let name = folder.name.length > 20 ? folder.name.substring(0, 20) + '...' : folder.name;
      this._originalTree.push({
        folderId: folder.id,
        name: name
      });
    });
  }

  private _findPathToFolder(folders: FolderTreeItem[], targetId: string, currentPath: FolderTreeItem[] = []): FolderTreeItem[] {
    for (const folder of folders) {
      const newPath = [...currentPath, folder];

      if (folder.id === targetId) {
        return newPath;
      }

      if (folder.children.length > 0) {
        const result = this._findPathToFolder(folder.children, targetId, newPath);
        if (result.length > 0) {
          return result;
        }
      }
    }
    return [];
  }

  private _checkForOverflow() {
    if (this._originalTree.length <= 1) {
      this.visibleFolders = this._originalTree;
      this.hiddenFolders = [];
      this.showMenu = false;
      return;
    }

    this.visibleFolders = [...this._originalTree];
    this.hiddenFolders = [];
    this.showMenu = false;
    this.changeDetectorRef.detectChanges();

    const containerWidth = this.elementRef.nativeElement.offsetWidth;
    if (containerWidth === 0) {
      // Container not sized yet, parent component may be loading
      setTimeout(() => this._checkForOverflow(), 100);
      return;
    }

    const pathElement = this.folderPathElement?.nativeElement;
    if (!pathElement) {
      return;
    }

    // Check if truncation is needed
    if (pathElement.scrollWidth <= containerWidth) {
      return;
    }

    // Path is overflowing its parent element; truncate it
    this._truncateTree(containerWidth, () => pathElement.scrollWidth);
  }

  private _truncateTree(containerWidth: number, getScrollWidth: () => number): void {
    // Start with all folders visible
    this.visibleFolders = [...this._originalTree];
    this.hiddenFolders = [];
    this.showMenu = false;

    // Keep moving folders to hidden until it fits
    while (this.visibleFolders.length > 1) {
      // Move the first folder to hidden
      const folderToHide = this.visibleFolders.shift();
      this.hiddenFolders.push(folderToHide);
      this.showMenu = true;

      // Update the view
      this.changeDetectorRef.detectChanges();

      // Check if it fits now
      if (getScrollWidth() <= containerWidth) {
        return;
      }
    }

    // If we get here, we've hidden all but the last folder and it still doesn't fit.
    // In this case, we'll show just the last folder and keep the rest in the menu.
    this.visibleFolders = [this._originalTree[this._originalTree.length - 1]];
    this.hiddenFolders = this._originalTree.slice(0, -1);
    this.showMenu = true;
  }

  clickFolder(pathOrId: string) {
    this.folderClicked.emit(pathOrId ?? '');
  }
}
